import { useEffect, useMemo, useState } from "react";
import { Network, Users, Sparkles } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// NetworkLinksPanel — ties a dossier to one or several partners
// of the Réseau XV directory and lets the admin pick:
//  · associated partners
//  · a prescriber interlocutor (the one who introduced the dossier)
//  · several mobilized interlocutors (working on the operation)
//  · a free-form origin field (Réseau, Recommandation, Salon…)
// ─────────────────────────────────────────────────────────────

export function NetworkLinksPanel({ dossier, partners, onDossierUpdate }) {
    const dossierId = dossier.id;
    const [state, setState] = useState({
        partner_ids: dossier.partner_ids || [],
        prescriber_interlocutor_id: dossier.prescriber_interlocutor_id || null,
        mobilized_interlocutor_ids: dossier.mobilized_interlocutor_ids || [],
        origin: dossier.origin || "",
    });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        setState({
            partner_ids: dossier.partner_ids || [],
            prescriber_interlocutor_id:
                dossier.prescriber_interlocutor_id || null,
            mobilized_interlocutor_ids: dossier.mobilized_interlocutor_ids || [],
            origin: dossier.origin || "",
        });
    }, [
        dossier.id,
        dossier.partner_ids,
        dossier.prescriber_interlocutor_id,
        dossier.mobilized_interlocutor_ids,
        dossier.origin,
    ]);

    // Flatten interlocutors of selected partners
    const interlocutorsPool = useMemo(() => {
        const selected = new Set(state.partner_ids);
        const out = [];
        for (const p of partners || []) {
            if (!selected.has(p.id)) continue;
            for (const it of p.interlocutors || []) {
                out.push({
                    id: it.id,
                    partner_id: p.id,
                    partner_name: p.name,
                    full_name: `${it.first_name || ""} ${it.last_name || ""}`.trim(),
                    role_title: it.role_title || "",
                });
            }
        }
        return out;
    }, [state.partner_ids, partners]);

    const flash = (m) => {
        setMsg(m);
        setTimeout(() => setMsg(""), 2500);
    };

    const save = async (overrides = {}) => {
        setBusy(true);
        try {
            const payload = { ...state, ...overrides };
            const { data } = await cockpit.put(
                `/dossiers/${dossierId}/network-links`,
                payload,
            );
            onDossierUpdate?.(data);
            setState({
                partner_ids: data.partner_ids || [],
                prescriber_interlocutor_id:
                    data.prescriber_interlocutor_id || null,
                mobilized_interlocutor_ids:
                    data.mobilized_interlocutor_ids || [],
                origin: data.origin || "",
            });
            flash("Liens réseau enregistrés ✓");
        } catch (e) {
            flash(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const togglePartner = (pid) => {
        const next = state.partner_ids.includes(pid)
            ? state.partner_ids.filter((x) => x !== pid)
            : [...state.partner_ids, pid];
        // If we unselect a partner, also drop any of its interlocutors
        // from mobilized + prescriber
        const partner = (partners || []).find((p) => p.id === pid);
        let prescriber = state.prescriber_interlocutor_id;
        let mobilized = state.mobilized_interlocutor_ids;
        if (!next.includes(pid) && partner) {
            const ids = (partner.interlocutors || []).map((i) => i.id);
            if (prescriber && ids.includes(prescriber)) prescriber = null;
            mobilized = mobilized.filter((iid) => !ids.includes(iid));
        }
        save({
            partner_ids: next,
            prescriber_interlocutor_id: prescriber,
            mobilized_interlocutor_ids: mobilized,
        });
    };

    const toggleMobilized = (iid) => {
        const next = state.mobilized_interlocutor_ids.includes(iid)
            ? state.mobilized_interlocutor_ids.filter((x) => x !== iid)
            : [...state.mobilized_interlocutor_ids, iid];
        save({ mobilized_interlocutor_ids: next });
    };

    const setPrescriber = (iid) => {
        save({ prescriber_interlocutor_id: iid || null });
    };

    return (
        <section
            data-testid="cockpit-network-links"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="mb-10">
                <div className="overline text-[#d4af37]/70 mb-3">
                    <Network
                        strokeWidth={1}
                        size={14}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                    Liens avec le Réseau XV
                </div>
                <h2
                    className="font-serif text-[26px] sm:text-[30px] text-[#f4ecd8] leading-tight"
                    style={{ fontWeight: 400 }}
                >
                    Structures & interlocuteurs mobilisés
                </h2>
                <p className="mt-2 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                    Rattachez ce dossier à une ou plusieurs structures du
                    Réseau XV. Désignez l'interlocuteur prescripteur ainsi
                    que ceux mobilisés sur l'opération — le calcul du CA
                    réseau s'ajustera automatiquement.
                </p>
                {msg && (
                    <div
                        data-testid="network-links-msg"
                        className="mt-4 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                    >
                        {msg}
                    </div>
                )}
            </div>

            <div className="grid lg:grid-cols-12 gap-10">
                {/* Partners picker */}
                <div className="lg:col-span-5">
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Structures associées
                    </div>
                    <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light mb-5">
                        Cochez les structures intervenant dans le dossier.
                    </p>
                    <div className="max-h-[280px] overflow-y-auto pr-2 space-y-1">
                        {(partners || []).length === 0 && (
                            <p className="text-[12px] text-[#f4ecd8]/35 italic">
                                Aucune structure au répertoire.
                            </p>
                        )}
                        {(partners || []).map((p) => {
                            const active = state.partner_ids.includes(p.id);
                            return (
                                <label
                                    key={p.id}
                                    data-testid={`link-partner-${p.id}`}
                                    className="flex items-center gap-3 py-2 px-1 cursor-pointer group"
                                >
                                    <span
                                        className={`w-4 h-4 border flex items-center justify-center transition-colors duration-300 ${
                                            active
                                                ? "border-[#d4af37] bg-[#d4af37]/20"
                                                : "border-white/20 group-hover:border-[#d4af37]/50"
                                        }`}
                                    >
                                        {active && (
                                            <span className="block w-1.5 h-1.5 bg-[#d4af37]" />
                                        )}
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={active}
                                        disabled={busy}
                                        onChange={() => togglePartner(p.id)}
                                        className="sr-only"
                                    />
                                    <span
                                        className={`font-serif text-[13.5px] ${
                                            active
                                                ? "text-[#f4ecd8]"
                                                : "text-[#f4ecd8]/70 group-hover:text-[#f4ecd8]/90"
                                        }`}
                                    >
                                        {p.name}
                                        <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55">
                                            {(p.interlocutors || []).length}
                                        </span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Interlocutors picker (prescriber + mobilized) */}
                <div className="lg:col-span-7 space-y-10">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">
                            <Sparkles
                                strokeWidth={1}
                                size={12}
                                className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                            />
                            Prescripteur
                        </div>
                        <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light mb-4">
                            Qui a introduit ce dossier ? Sélectionnez parmi
                            les interlocuteurs des structures associées.
                        </p>
                        <select
                            data-testid="prescriber-select"
                            value={state.prescriber_interlocutor_id || ""}
                            disabled={busy || interlocutorsPool.length === 0}
                            onChange={(e) => setPrescriber(e.target.value)}
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                        >
                            <option value="" className="bg-[#0b1220]">
                                — Aucun désigné —
                            </option>
                            {interlocutorsPool.map((it) => (
                                <option
                                    key={it.id}
                                    value={it.id}
                                    className="bg-[#0b1220]"
                                >
                                    {`${it.full_name || "Sans nom"} · ${it.partner_name}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">
                            <Users
                                strokeWidth={1}
                                size={12}
                                className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                            />
                            Interlocuteurs mobilisés
                        </div>
                        <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light mb-4">
                            Personnes actives sur l'opération.
                        </p>
                        {interlocutorsPool.length === 0 ? (
                            <p className="text-[12px] text-[#f4ecd8]/35 italic font-light">
                                Aucun interlocuteur disponible — commencez
                                par associer une structure.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {interlocutorsPool.map((it) => {
                                    const active =
                                        state.mobilized_interlocutor_ids.includes(
                                            it.id,
                                        );
                                    return (
                                        <button
                                            key={it.id}
                                            type="button"
                                            data-testid={`mobilized-${it.id}`}
                                            onClick={() => toggleMobilized(it.id)}
                                            disabled={busy}
                                            className={`text-[11px] tracking-[0.16em] px-3 py-1.5 border transition-all duration-500 ${
                                                active
                                                    ? "border-[#d4af37] bg-[#d4af37]/15 text-[#d4af37]"
                                                    : "border-white/15 text-[#f4ecd8]/60 hover:border-[#d4af37]/40 hover:text-[#f4ecd8]"
                                            }`}
                                        >
                                            {it.full_name || "Sans nom"}
                                            <span className="ml-2 text-[#f4ecd8]/40">
                                                · {it.partner_name}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">
                            Origine du dossier
                        </div>
                        <input
                            type="text"
                            data-testid="origin-input"
                            value={state.origin}
                            onChange={(e) =>
                                setState({ ...state, origin: e.target.value })
                            }
                            onBlur={() => save()}
                            placeholder="Réseau, recommandation directe, salon privé…"
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
